// Canonical `/messages` link builders and search-param shape. Extracted from
// the messages route so any feature can link into a conversation WITHOUT
// pulling the heavy `MessagesClient` route module into its bundle (the
// bottom-right messaging dock, mounted in the always-loaded Shell, is the
// motivating consumer — importing from the route file would eager-load the
// full inbox into the shell chunk). The route re-exports these for existing
// callers, so this is the single source of truth for the param names.

export type MessagesTab = 'all' | 'unread' | 'requests' | 'archived';

export type MessagesSearch = {
  c?: string;
  compose?: string;
  draft?: string;
  q?: string;
  tab?: MessagesTab;
};

/**
 * `compose` sentinel meaning "open the composer with no recipient selected"
 * — the share-to-Circle entry point, where the viewer picks who to send to.
 * Every other `compose` value is a member id that find-or-creates a DM, so
 * the sentinel is deliberately not a UUID: the `isUuid` gate in the route
 * rejects it and no conversation is created until the viewer chooses a
 * recipient.
 */
export const COMPOSE_NEW = 'new';

/**
 * The options every `/messages` link is built from.
 *
 * Pass either an existing `conversationId` (lands on that thread), a
 * `composeUserId` (route find-or-creates a DM with that user), or
 * `composeNew` (opens the composer with no recipient, for the viewer to
 * choose). Optional `draft` seeds the new conversation's composer with a
 * body. Optional `messageId` adds the deep-link hash so the client scrolls to
 * a specific message. Optional `tab` selects the inbox scope (`all` is the
 * default and is left out of the URL so the canonical link stays clean).
 */
export type MessagesLinkOpts = {
  readonly conversationId?: string;
  readonly composeUserId?: string;
  readonly composeNew?: boolean;
  readonly draft?: string;
  readonly messageId?: string;
  readonly tab?: MessagesTab;
};

/**
 * Single source of truth for the `/messages` search-param shape. All callers
 * route through here (or `messagesHref`, which serializes this) so the params
 * stay in lockstep with the route's `validateSearch` — a future rename ripples
 * through one file, not the codebase.
 *
 * Use this with `navigate({ to: '/messages', search: messagesSearch(...) })`.
 * Prefer the search object over a pre-built `messagesHref` string: on the old
 * router (≤1.95) the string round trip dropped percent-encoded newlines and
 * silently flattened the multi-line `draft` body. Today's router preserves
 * them (pinned by messages-link.test.ts), so the href form is safe too — the
 * search-object form stays canonical because it typechecks the params.
 */
export function messagesSearch(opts: MessagesLinkOpts): MessagesSearch {
  return {
    ...(opts.conversationId ? { c: opts.conversationId } : {}),
    ...(opts.composeUserId
      ? { compose: opts.composeUserId }
      : opts.composeNew
        ? { compose: COMPOSE_NEW }
        : {}),
    ...(opts.draft ? { draft: opts.draft } : {}),
    ...(opts.tab !== undefined && opts.tab !== 'all' ? { tab: opts.tab } : {}),
  };
}

/**
 * The same link as an href string, for anchors and `<Link to>` targets that
 * take one. Serializes `messagesSearch` and appends the deep-link hash.
 */
export function messagesHref(opts: MessagesLinkOpts): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(messagesSearch(opts))) {
    if (value !== undefined) params.set(key, value);
  }
  const qs = params.toString();
  const hash = opts.messageId ? `#message-${opts.messageId}` : '';
  return `/messages${qs ? `?${qs}` : ''}${hash}`;
}
